import { MenusRequestMessageSchema, Message, CollectionRegistry } from '../types';
import { logger } from '../utils/logger';

/**
 * Handle unified menus management request
 * Supports operations: create, update, delete, getAll, getOne, getRootMenus, getChildMenus, getHierarchy, query, reorder
 * Routes requests to backend collections
 */
export async function handleMenusRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  // Helper function to execute collection operations
  const executeCollection = async (collection: string, op: string, params: any): Promise<any> => {
    const handler = collections[collection]?.[op];
    if (!handler) {
      throw new Error(`Collection operation ${collection}.${op} not found`);
    }
    return await handler(params);
  };

  try {
    const request = MenusRequestMessageSchema.parse(data);
    const { id, payload, from } = request;
    const { operation, data: requestData } = payload;

    // Extract common fields
    const menuId = requestData?.id;
    const name = requestData?.name;
    const componentName = requestData?.componentName;
    const icon = requestData?.icon;
    const userMessage = requestData?.userMessage;
    const parentId = requestData?.parentId;
    const sortOrder = requestData?.sortOrder;
    const props = requestData?.props;
    const isActive = requestData?.isActive;
    // Query operation fields
    const filters = requestData?.filters;
    const limit = requestData?.limit;
    const sort = requestData?.sort;
    // Reorder operation fields
    const items = requestData?.items;

    // Route to appropriate operation handler
    switch (operation) {
      case 'create':
        await handleCreate(id, name, componentName, icon, userMessage, parentId, sortOrder, props, isActive, executeCollection, sendMessage, from.id);
        break;

      case 'update':
        await handleUpdate(id, menuId, name, componentName, icon, userMessage, parentId, sortOrder, props, isActive, executeCollection, sendMessage, from.id);
        break;

      case 'delete':
        await handleDelete(id, menuId, executeCollection, sendMessage, from.id);
        break;

      case 'getAll':
        await handleGetAll(id, executeCollection, sendMessage, from.id);
        break;

      case 'getOne':
        await handleGetOne(id, menuId, executeCollection, sendMessage, from.id);
        break;

      case 'getRootMenus':
        await handleGetRootMenus(id, executeCollection, sendMessage, from.id);
        break;

      case 'getChildMenus':
        await handleGetChildMenus(id, parentId, executeCollection, sendMessage, from.id);
        break;

      case 'getHierarchy':
        await handleGetHierarchy(id, executeCollection, sendMessage, from.id);
        break;

      case 'query':
        await handleQuery(id, filters, limit, sort, executeCollection, sendMessage, from.id);
        break;

      case 'reorder':
        await handleReorder(id, items, executeCollection, sendMessage, from.id);
        break;

      default:
        sendResponse(id, {
          success: false,
          error: `Unknown operation: ${operation}`
        }, sendMessage, from.id);
    }

  } catch (error) {
    logger.error('Failed to handle menus request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Handle create menu operation
 */
async function handleCreate(
  id: string,
  name: string | undefined,
  componentName: string | undefined,
  icon: string | undefined,
  userMessage: string | undefined,
  parentId: number | null | undefined,
  sortOrder: number | undefined,
  props: Record<string, unknown> | undefined,
  isActive: boolean | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!name) {
    sendResponse(id, {
      success: false,
      error: 'name is required'
    }, sendMessage, clientId);
    return;
  }

  if (!componentName) {
    sendResponse(id, {
      success: false,
      error: 'componentName is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('menus', 'create', {
      name,
      componentName,
      icon,
      userMessage,
      parentId,
      sortOrder,
      props,
      isActive
    });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Menu created successfully'
    }, sendMessage, clientId);

    logger.info(`Menu created: ID ${result.data?.id}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create menu'
    }, sendMessage, clientId);
  }
}

/**
 * Handle update menu operation
 */
async function handleUpdate(
  id: string,
  menuId: number | undefined,
  name: string | undefined,
  componentName: string | undefined,
  icon: string | undefined,
  userMessage: string | undefined,
  parentId: number | null | undefined,
  sortOrder: number | undefined,
  props: Record<string, unknown> | undefined,
  isActive: boolean | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!menuId) {
    sendResponse(id, {
      success: false,
      error: 'Menu ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('menus', 'update', {
      id: menuId,
      name,
      componentName,
      icon,
      userMessage,
      parentId,
      sortOrder,
      props,
      isActive
    });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Menu updated successfully'
    }, sendMessage, clientId);

    logger.info(`Menu updated: ID ${menuId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update menu'
    }, sendMessage, clientId);
  }
}

/**
 * Handle delete menu operation
 */
async function handleDelete(
  id: string,
  menuId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!menuId) {
    sendResponse(id, {
      success: false,
      error: 'Menu ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('menus', 'delete', { id: menuId });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Menu deleted successfully'
    }, sendMessage, clientId);

    logger.info(`Menu deleted: ID ${menuId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete menu'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get all menus operation
 */
async function handleGetAll(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('menus', 'getAll', {});

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Retrieved ${result.count} menus`
    }, sendMessage, clientId);

    logger.info(`Retrieved all menus (count: ${result.count})`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get menus'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get one menu operation
 */
async function handleGetOne(
  id: string,
  menuId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!menuId) {
    sendResponse(id, {
      success: false,
      error: 'Menu ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('menus', 'getOne', { id: menuId });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: `Retrieved menu ID ${menuId}`
    }, sendMessage, clientId);

    logger.info(`Retrieved menu: ID ${menuId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get menu'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get root menus operation (top-level sidebar items)
 */
async function handleGetRootMenus(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('menus', 'getRootMenus', {});

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Retrieved ${result.count} root menus`
    }, sendMessage, clientId);

    logger.info(`Retrieved root menus (count: ${result.count})`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get root menus'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get child menus operation
 */
async function handleGetChildMenus(
  id: string,
  parentId: number | null | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (parentId === undefined || parentId === null) {
    sendResponse(id, {
      success: false,
      error: 'parentId is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('menus', 'getChildMenus', { parentId });

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Retrieved ${result.count} child menus for parent ${parentId}`
    }, sendMessage, clientId);

    logger.info(`Retrieved child menus for parent ${parentId} (count: ${result.count})`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get child menus'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get menus hierarchy operation (nested structure)
 */
async function handleGetHierarchy(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('menus', 'getHierarchy', {});

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Retrieved menus hierarchy with ${result.count} root items`
    }, sendMessage, clientId);

    logger.info(`Retrieved menus hierarchy (root count: ${result.count})`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get menus hierarchy'
    }, sendMessage, clientId);
  }
}

/**
 * Handle query menus operation with filters
 */
async function handleQuery(
  id: string,
  filters: any,
  limit: number | undefined,
  sort: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('menus', 'query', {
      filters: filters || {},
      limit,
      sort
    });

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Query returned ${result.count} menus`
    }, sendMessage, clientId);

    logger.info(`Query returned ${result.count} menus`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to query menus'
    }, sendMessage, clientId);
  }
}

/**
 * Handle reorder menus operation
 */
async function handleReorder(
  id: string,
  items: Array<{ id: number; sortOrder: number }> | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!items || !Array.isArray(items) || items.length === 0) {
    sendResponse(id, {
      success: false,
      error: 'items array is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('menus', 'reorder', { items });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: `Reordered ${items.length} menus successfully`
    }, sendMessage, clientId);

    logger.info(`Reordered ${items.length} menus`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to reorder menus'
    }, sendMessage, clientId);
  }
}

/**
 * Send menus response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any; count?: number; message?: string },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'MENUS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'admin',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
